/**
 * TrustedProviders.jsx
 *
 * Public directory of service-orientation-first pet providers — vets,
 * stores, trainers, sitters, etc. — that AADC members and admins have
 * verified as independently/family/employee-owned (i.e., NOT rolled-up by
 * private equity).
 *
 * Why this page exists:
 *   Most pet directories sort by reviews. None tell you who's owned by
 *   VCA/Mars or PetSmart/BC Partners. AADC's competitive moat is the
 *   community-vouched signal that AI directories can't replicate.
 *
 * Members can nominate (auth required). Admins verify in /admin/independents.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Building2, Scissors, GraduationCap, Stethoscope, Brain, Home, Camera,
  Plus, MapPin, ExternalLink, BadgeCheck, Award, Star, Search, ChevronRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import NominateProviderDialog from '../components/independents/NominateProviderDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  vet: { label: 'Vets', icon: Stethoscope },
  store: { label: 'Stores', icon: Building2 },
  groomer: { label: 'Groomers', icon: Scissors },
  trainer: { label: 'Trainers', icon: GraduationCap },
  behaviorist: { label: 'Behaviorists', icon: Brain },
  sitter: { label: 'Sitters', icon: Home },
  walker: { label: 'Walkers', icon: Home },
  daycare: { label: 'Daycares', icon: Home },
  boarder: { label: 'Boarders', icon: Home },
  photographer: { label: 'Photographers', icon: Camera },
  other: { label: 'Other', icon: Building2 },
};

const OWNERSHIP_LABEL = {
  independent: 'Independent',
  family_owned: 'Family-owned',
  employee_owned: 'Employee-owned',
  cooperative: 'Cooperative',
  nonprofit: 'Nonprofit',
  academic: 'Academic',
  pe_owned: 'PE-owned',
  corporate: 'Corporate',
  unknown: 'Unverified',
};

const OWNERSHIP_TONE = {
  independent: 'bg-amber-100 text-amber-900 border-amber-200',
  family_owned: 'bg-rose-100 text-rose-900 border-rose-200',
  employee_owned: 'bg-emerald-100 text-emerald-900 border-emerald-200',
  cooperative: 'bg-emerald-100 text-emerald-900 border-emerald-200',
  nonprofit: 'bg-sky-100 text-sky-900 border-sky-200',
  academic: 'bg-indigo-100 text-indigo-900 border-indigo-200',
  pe_owned: 'bg-red-100 text-red-900 border-red-300',
  corporate: 'bg-stone-200 text-stone-700 border-stone-300',
  unknown: 'bg-stone-100 text-stone-600 border-stone-200',
};

const SERVICE_ORIENTED = new Set([
  'independent', 'family_owned', 'employee_owned', 'cooperative', 'nonprofit', 'academic',
]);

export default function TrustedProviders() {
  const { user } = useAuth();
  const [providers, setProviders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('');
  const [ownershipFilter, setOwnershipFilter] = useState('all');
  const [searchQ, setSearchQ] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [cities, setCities] = useState([]);
  const [nominateOpen, setNominateOpen] = useState(false);

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter !== 'all') params.set('type', typeFilter);
      if (stateFilter.trim()) params.set('state', stateFilter.trim().toUpperCase());
      if (ownershipFilter !== 'all') params.set('ownership', ownershipFilter);
      if (searchQ.trim()) params.set('search', searchQ.trim());
      if (showAll) params.set('service_oriented_only', 'false');
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/independents?${params.toString()}`),
        axios.get(`${API}/independents/stats`),
      ]);
      setProviders(r1.data || []);
      setStats(r2.data || null);
      // Lazy-load cities once (only on first call when cities array is empty)
      if (cities.length === 0) {
        try {
          const r3 = await axios.get(`${API}/independents/cities?limit=24`);
          setCities(r3.data || []);
        } catch (e) { /* non-fatal */ }
      }
    } catch (err) {
      console.error(err);
      toast.error('Could not load providers');
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch on filter change (debounced for search)
  useEffect(() => {
    const t = setTimeout(fetchProviders, searchQ ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, stateFilter, ownershipFilter, searchQ, showAll]);

  const grouped = useMemo(() => {
    const out = {};
    for (const p of providers) {
      (out[p.type] = out[p.type] || []).push(p);
    }
    // Ensure featured surface first inside each group
    for (const k of Object.keys(out)) {
      out[k].sort((a, b) => {
        if (a.status === b.status) return a.name.localeCompare(b.name);
        return a.status === 'featured' ? -1 : 1;
      });
    }
    return out;
  }, [providers]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white" data-testid="trusted-providers-page">
      <SEO
        title="Trusted Independent Providers — Vets, Stores, Trainers"
        description="AADC's member-curated directory of pet service providers that are independently, family, or employee-owned — not rolled up by private equity. Vets, stores, trainers, sitters, and more."
        url="/trusted-providers"
        keywords={['independent vets', 'family-owned pet stores', 'employee-owned pet store', 'force-free trainer', 'non-corporate vet', 'AADC trusted providers']}
      />

      {/* Hero */}
      <section className="px-4 sm:px-6 pt-12 pb-8 max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="max-w-3xl">
            <Badge className="mb-3 bg-amber-100 text-amber-900 border-amber-200" data-testid="hero-badge">
              <Award className="w-3.5 h-3.5 mr-1" /> Service over profit
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" data-testid="hero-title">
              Trusted Independent Providers
            </h1>
            <p className="mt-4 text-base sm:text-lg text-stone-600 leading-relaxed">
              Vets, stores, and trainers our members vouch for — independently,
              family, or employee-owned. We keep private-equity roll-ups off this list
              so you can find the people who answer to their clients, not a fund.
            </p>
          </div>
          <Button
            size="lg"
            onClick={() => {
              if (!user) {
                toast.error('Please sign in to nominate a provider');
                return;
              }
              setNominateOpen(true);
            }}
            data-testid="nominate-cta"
            className="self-start sm:self-end"
          >
            <Plus className="w-4 h-4 mr-2" /> Nominate a Provider
          </Button>
        </div>

        {stats && (
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4" data-testid="stats-row">
            <StatCard label="Total listed" value={stats.total_verified} />
            <StatCard label="Independents" value={stats.by_ownership?.independent || 0} />
            <StatCard label="Family-owned" value={stats.by_ownership?.family_owned || 0} />
            <StatCard label="Employee/Co-op" value={(stats.by_ownership?.employee_owned || 0) + (stats.by_ownership?.cooperative || 0)} />
          </div>
        )}

        {cities.length > 0 && (
          <div className="mt-6" data-testid="city-rail">
            <div className="text-xs uppercase tracking-wide text-stone-500 mb-2">Browse by city</div>
            <div className="flex flex-wrap gap-2">
              {cities.map((c) => (
                <Link
                  key={c.slug}
                  to={`/trusted-providers/${c.slug}`}
                  className="text-sm bg-white border border-stone-200 rounded-full px-3 py-1 hover:bg-amber-50 hover:border-amber-300 transition-colors"
                  data-testid={`city-chip-${c.slug}`}
                >
                  {c.city}{c.state ? `, ${c.state}` : ''} <span className="text-stone-400">·{c.count}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Filters */}
      <section className="px-4 sm:px-6 max-w-6xl mx-auto sticky top-0 z-10 bg-stone-50/95 backdrop-blur-sm py-3 border-y border-stone-200">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Select value={typeFilter} onValueChange={setTypeFilter} data-testid="filter-type">
            <SelectTrigger data-testid="filter-type-trigger"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {Object.entries(TYPE_META).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={ownershipFilter} onValueChange={setOwnershipFilter} data-testid="filter-ownership">
            <SelectTrigger data-testid="filter-ownership-trigger"><SelectValue placeholder="Ownership" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All ownership</SelectItem>
              {Object.entries(OWNERSHIP_LABEL).map(([k, label]) => (
                <SelectItem key={k} value={k}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            placeholder="State (e.g. WA, CA)"
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            maxLength={3}
            data-testid="filter-state"
          />
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              placeholder="Search name or city"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              className="pl-9"
              data-testid="filter-search"
            />
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between">
          <label className="inline-flex items-center gap-2 text-sm text-stone-600 cursor-pointer select-none" data-testid="show-all-toggle-label">
            <input
              type="checkbox"
              className="rounded border-stone-300"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
              data-testid="show-all-toggle"
            />
            <span>Show PE-owned & corporate providers <span className="text-stone-400">(hidden by default — see why &darr;)</span></span>
          </label>
          {showAll && (
            <Badge className="bg-red-100 text-red-900 border-red-300" data-testid="showing-all-badge">
              Showing all providers — including AVOID list
            </Badge>
          )}
        </div>
      </section>

      {/* Results */}
      <section className="px-4 sm:px-6 max-w-6xl mx-auto py-8 space-y-10">
        {loading && (
          <div className="text-center py-16" data-testid="loading">
            <div className="inline-block w-8 h-8 border-4 border-stone-300 border-t-amber-600 rounded-full animate-spin" />
          </div>
        )}
        {!loading && providers.length === 0 && (
          <Card data-testid="empty-state">
            <CardContent className="p-10 text-center space-y-4">
              <p className="text-stone-600">No providers match those filters yet.</p>
              <Button onClick={() => { setTypeFilter('all'); setStateFilter(''); setOwnershipFilter('all'); setSearchQ(''); }} variant="outline" data-testid="clear-filters">
                Clear filters
              </Button>
            </CardContent>
          </Card>
        )}
        {!loading && Object.entries(grouped).map(([type, list]) => {
          const meta = TYPE_META[type] || TYPE_META.other;
          const Icon = meta.icon;
          return (
            <div key={type} data-testid={`group-${type}`}>
              <div className="flex items-center gap-2 mb-4">
                <Icon className="w-5 h-5 text-amber-700" />
                <h2 className="text-xl font-semibold tracking-tight">{meta.label}</h2>
                <span className="text-sm text-stone-500">({list.length})</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {list.map((p) => (
                  <ProviderCard key={p.id} provider={p} />
                ))}
              </div>
            </div>
          );
        })}
      </section>

      {/* Footer note: AVOID list */}
      <section className="px-4 sm:px-6 max-w-6xl mx-auto pb-16">
        <Card className="bg-stone-100 border-stone-200">
          <CardContent className="p-6 space-y-2">
            <h3 className="font-semibold text-stone-900">Why these and not others?</h3>
            <p className="text-sm text-stone-600 leading-relaxed">
              We intentionally <strong>exclude</strong> providers we know to be owned by
              private-equity roll-ups (VCA, Banfield, BluePearl, Thrive, NVA, PetVet,
              Heart+Paw, Bond Vet, Small Door, PetSmart, Petco, Hollywood Feed, Pet Valu,
              Dogtopia, Camp Bow Wow, and similar). They may still deliver great service —
              but their ownership structure means decisions flow upward to a fund manager,
              not outward to the people they serve. If your favorite provider was recently
              acquired and we haven't caught it, <Link to="/contact" className="underline">let us know</Link>.
            </p>
          </CardContent>
        </Card>
      </section>

      <NominateProviderDialog
        open={nominateOpen}
        onClose={() => setNominateOpen(false)}
        onSubmitted={() => { setNominateOpen(false); toast.success('Thanks — admins will review your nomination shortly.'); fetchProviders(); }}
      />
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="bg-white border border-stone-200 rounded-lg px-4 py-3" data-testid={`stat-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
      <div className="text-2xl font-bold text-stone-900">{value}</div>
      <div className="text-xs text-stone-500 uppercase tracking-wide">{label}</div>
    </div>
  );
}

function ProviderCard({ provider }) {
  const p = provider;
  const reviewGoogle = p.review_summaries?.find((r) => r.source === 'google');
  const isAvoid = !SERVICE_ORIENTED.has(p.ownership_type);
  return (
    <Card
      className={`hover:shadow-md transition-shadow ${isAvoid ? 'border-red-200 bg-red-50/30 opacity-90' : 'border-stone-200'}`}
      data-testid={`provider-card-${p.id}`}
    >
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              {p.status === 'featured' && (
                <BadgeCheck className="w-4 h-4 text-amber-600 flex-shrink-0" data-testid="featured-badge" />
              )}
              {isAvoid && (
                <span className="text-xs font-bold text-red-700 bg-red-100 border border-red-300 rounded px-1.5 py-0.5 flex-shrink-0" data-testid={`avoid-pill-${p.id}`}>
                  AVOID
                </span>
              )}
              <h3 className={`font-semibold truncate ${isAvoid ? 'text-stone-700' : 'text-stone-900'}`} data-testid={`provider-name-${p.id}`}>{p.name}</h3>
            </div>
            <div className="flex items-center gap-2 text-xs text-stone-500 mt-0.5">
              {p.city && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {p.city}{p.state ? `, ${p.state}` : ''}
                </span>
              )}
              {p.founded_year && <span>· est. {p.founded_year}</span>}
            </div>
          </div>
          {p.website && (
            <a
              href={p.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-stone-400 hover:text-stone-700 flex-shrink-0"
              data-testid={`provider-link-${p.id}`}
              title="Visit website"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5">
          <Badge
            className={`text-xs border ${OWNERSHIP_TONE[p.ownership_type] || 'bg-stone-100 text-stone-700'}`}
            data-testid={`ownership-${p.id}`}
          >
            {OWNERSHIP_LABEL[p.ownership_type] || p.ownership_type}
          </Badge>
          {p.quality_certifications?.slice(0, 3).map((c) => (
            <Badge key={c} variant="outline" className="text-xs">{c}</Badge>
          ))}
        </div>

        {p.service_notes && (
          <p className="text-sm text-stone-600 leading-relaxed line-clamp-3" data-testid={`provider-notes-${p.id}`}>
            {p.service_notes}
          </p>
        )}

        {reviewGoogle && (
          <div className="flex items-center gap-1 text-xs text-stone-500">
            <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
            <span className="font-medium text-stone-700">{reviewGoogle.rating}</span>
            {reviewGoogle.count && <span>· {reviewGoogle.count.toLocaleString()} Google reviews</span>}
          </div>
        )}

        {p.nominated_by_name && p.nominated_by_name !== 'AADC Editorial' && (
          <div className="text-xs text-stone-400 pt-1 border-t border-stone-100">
            Nominated by @{p.nominated_by_name}
          </div>
        )}
        {p.claimed_by_user_id && (
          <div className="text-xs text-emerald-700 inline-flex items-center gap-1 bg-emerald-50 rounded px-2 py-0.5">
            <BadgeCheck className="w-3 h-3" /> Claimed by owner
          </div>
        )}
      </CardContent>
    </Card>
  );
}
